import * as React from "react";
import { useState, useEffect } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

// Edit Facility Details (slice A4) -- one coherent administrative concern: the facility's own
// active flag, contact email, and notes. Distinct from Edit Facility Ownership (BDM / PaceSetter /
// NLL), which has its own dialog and its own endpoint.
//
// SAVE SEMANTICS DIFFER FROM THE OWNERSHIP DIALOG, ON PURPOSE. `facility-update` performs a FULL
// OVERWRITE of exactly lksi_is_active / lksi_email / lksi_notes, so this dialog always sends all
// three -- omitting one would write NULL. That is safe because those three columns are precisely
// this dialog's concern and the handler leaves the ownership columns untouched. Change tracking is
// still used, but only to decide whether Save is enabled, not to trim the payload.
export default function EditFacilityDetailsDialog(props) {
  const open = props.open;
  const onClose = props.onClose;
  const facility = props.facility;
  const onSave = props.onSave;
  const onSaved = props.onSaved;

  const [isActive, setIsActive] = useState(true);
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Re-seed from the current values on every open (dialog contract rule 1).
  useEffect(() => {
    if (!open) return;
    setIsActive(facility.isActive);
    setEmail(facility.email ?? "");
    setNotes(facility.notes ?? "");
    setError(null);
  }, [open, facility]);

  const hasChanges =
    isActive !== facility.isActive ||
    email !== (facility.email ?? "") ||
    notes !== (facility.notes ?? "");

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      // All three fields, every time -- see the full-overwrite note above. Empty strings are sent
      // as null so "cleared" is stored as NULL rather than as an empty string.
      await onSave({
        dim_facilityID: facility.id,
        isLKSIActive: isActive ? 1 : 0,
        locationLKSIEmail: email.trim() === "" ? null : email.trim(),
        locationLKSINotes: notes.trim() === "" ? null : notes,
      });
      if (onSaved) onSaved(`${facility.name} details updated`);
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{facility.name} — Facility Details</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={isActive}
                onChange={(event) => setIsActive(event.target.checked)}
                disabled={saving}
              />
            }
            label={
              <Typography variant="body2">
                Facility active {isActive ? "" : "— hidden from the default Active-only view"}
              </Typography>
            }
          />

          <TextField
            label="Facility email"
            size="small"
            fullWidth
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            disabled={saving}
            helperText="One or more addresses. Overrides the email carried on the warehouse facility record."
          />

          <TextField
            label="Facility notes"
            size="small"
            fullWidth
            multiline
            minRows={4}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            disabled={saving}
          />

          {error && <Alert severity="error">{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving} color="inherit">
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={saving || !hasChanges} variant="contained">
          {saving ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
